/**
 * Promise-based confirmation dialog.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 *
 * Replaces `window.confirm()`. Native confirm blocks the JS thread, cannot be
 * styled or themed, ignores the app's language and tone, is suppressible by
 * the browser after repeated use, and is invisible to Playwright without a
 * special handler. Radix `AlertDialog` gives focus trapping, Escape, restoring
 * focus to the trigger, and the right ARIA roles for free.
 *
 * The API is deliberately shaped like the thing it replaces, so a call site
 * changes by one word and an `await`:
 *
 *   if (!confirm('Delete this?')) return;          // before
 *   if (!(await confirm({ title: 'Delete this?' }))) return;   // after
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

export interface ConfirmOptions {
  /** The question, as a short sentence. */
  title: string;
  /** Optional detail — the consequence, not a restatement of the title. */
  description?: string;
  /** Names the action, e.g. "Delete user". Never "OK" (writing.md › Buttons). */
  confirmText?: string;
  cancelText?: string;
  /** Marks an irreversible action: the confirm button turns destructive. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  /** Settle the promise exactly once, whichever way the dialog closed —
   *  button, Escape, or an outside click all have to resolve it, or an
   *  `await confirm(...)` would hang forever. */
  const settle = useCallback((result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog.Root
        open={options !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-text-primary/40" />
          <AlertDialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-bg-surface p-6 shadow-lg"
          >
            <AlertDialog.Title className="text-lg font-semibold text-text-primary">
              {options?.title}
            </AlertDialog.Title>

            {options?.description && (
              <AlertDialog.Description className="mt-2 text-sm text-text-secondary">
                {options.description}
              </AlertDialog.Description>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-subtle"
                >
                  {options?.cancelText ?? 'Cancel'}
                </button>
              </AlertDialog.Cancel>

              <AlertDialog.Action asChild>
                <button
                  type="button"
                  onClick={() => settle(true)}
                  data-testid="confirm-dialog-confirm"
                  className={
                    options?.destructive
                      ? 'rounded-lg bg-qc-error px-4 py-2 text-sm font-medium text-text-inverse hover:opacity-90'
                      : 'rounded-lg bg-interactive-primary px-4 py-2 text-sm font-medium text-text-inverse hover:bg-interactive-primary-hover'
                  }
                >
                  {options?.confirmText ?? 'Confirm'}
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  return ctx;
}
